// @flow

import { parse } from 'query-string';
import { getSearch } from '../../router';
import type { State as RouterState } from '../../router/types';
import type {
  PackageContentTable,
  PackageShortcode,
  PackageOriginalName,
  Package,
  PackageCar,
} from '../../../types/pdb';
import type { State } from '../types';

import {
  getPdbItem,
} from './main';

import splitLetters from '../../../helpers/splitLetters';

type GlobalState = {
  pdb: State,
  router: RouterState,
};

export const getPackageIdFromUrl = (state: GlobalState): any => {
  const search = state.router ? getSearch(state) : window.location.search;
  const params = parse(search);
  return params.pkg;
};

export const getPackages = (state: GlobalState, pdbId: ?string = null): Package[] => {
  const pdbItem = getPdbItem(state, pdbId);
  return pdbItem.packages;
};

export const getPackage = (state: GlobalState, packageId: number, pdbId?: ?string): ?Package => {
  const pkgId = packageId || getPackageIdFromUrl(state);
  return getPackages(state, pdbId).find(({ id }) => id === pkgId);
};

export const hasDiscount = (state: GlobalState, packageId: number, pdbId?: ?string): boolean => {
  const pkgId = packageId || getPackageIdFromUrl(state);
  const currentPackage = getPackage(state, pkgId, pdbId);
  return currentPackage ? Number(currentPackage.mapped.discount) > 0 : false;
};

export const getPrice = (
  state: GlobalState,
  packageId: number,
  pdbId: ?string = null,
): number => {
  const pkgId = packageId || getPackageIdFromUrl(state);
  const currentPackage = getPackage(state, pkgId, pdbId);
  if (!currentPackage) {
    return 0;
  }
  // If the model has discount, we'll return the price_from as the price, else, we'll return the
  // actual price.
  return hasDiscount(state, packageId, pdbId)
    ? Number(currentPackage.price_from)
    : Number(currentPackage.price);
};

export const getDiscountPrice = (
  state: GlobalState,
  packageId: number,
  pdbId: ?string = null,
): number => {
  const pkgId = packageId || getPackageIdFromUrl(state);
  const currentPackage = getPackage(state, pkgId, pdbId);
  if (!currentPackage) {
    return 0;
  }
  // The discount price is always the actual package price (all possible discounts deducted)
  return parseInt(currentPackage.price, 10);
};

export const getName = (
  state: GlobalState,
  packageId?: ?string,
  pdbId?: ?string,
): ?string => {
  const pkgId = packageId || getPackageIdFromUrl(state);
  const currentPackage = getPackage(state, Number(pkgId), pdbId);
  return currentPackage ? currentPackage.name : null;
};

export const getFirstPackageName = (
  state: GlobalState,
  pdbId?: ?string,
): ?string => {
  const packages = getPackages(state, pdbId);
  return packages ? packages[0].name : null;
};

export const getDefaultImageUrl = (
  state: GlobalState,
  packageId: string,
  pdbId?: ?string,
): ?string => {
  const pkgId = packageId || getPackageIdFromUrl(state);
  const currentPackage = getPackage(state, Number(pkgId), pdbId);
  return currentPackage && currentPackage.default_image && currentPackage.default_image.url
    ? currentPackage.default_image.url : null;
};

export const getCheapestPackage = (packages: Array<Package>) => {
  const goal = 0;
  return packages.reduce((prev, curr) => {
    const currPrice = Math.abs(parseInt(curr.price, 10) - goal);
    const prevPrice = Math.abs(parseInt(prev.price, 10) - goal);
    return (currPrice < prevPrice ? curr : prev);
  });
};

export const isSoldOutPackage = (pdbPackage: Package) => {
  const { sold_out, stock_only, hasStock } = pdbPackage;
  return sold_out || (stock_only && !hasStock);
};

export const getDefaultPackage = (
  state: GlobalState,
  pdbId: ?string = null,
): ?Package => {
  const packages = getPackages(state, pdbId)
    .filter(pdbPackage => !isSoldOutPackage(pdbPackage));

  if (!packages || packages.length === 0) {
    return null;
  }

  if (packages.length === 1) {
    return packages[0];
  }

  return getCheapestPackage(packages);
};

// @todo we should put this correctly int he store instead of formatting it here
// (this is way more costly)
export const getShortcodes = (
  state: GlobalState,
  pdbId: ?string = null,
): Array<PackageShortcode> => {
  const packages = getPackages(state, pdbId);
  return packages.reduce((list, { id = 0, name = '' }) => (
    list.concat({ id, shortcode: splitLetters(name) })
  ), []);
};

export const getOriginalNames = (
  state: GlobalState,
  pdbId: ?string = null,
): Array<PackageOriginalName> => {
  const packages = getPackages(state, pdbId);
  return packages.reduce((list, { id = 0, name = '' }) => (
    list.concat({ id, originalname: name.replace(/-.*/, '') })
  ), []); // eslint-disable-line no-useless-escape
};

export const getEngine = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
  packageId?: string,
): string => {
  // If a package is selected,
  const pkgId = packageId || getPackageIdFromUrl(state);
  if (pkgId) {
    // we'll need to retrieve the package.
    const currentPackage = getPackage(state, Number(pkgId), pdbId);
    // If the package can be retrieved successfully,
    if (currentPackage) {
      // we can return the package engine.
      return currentPackage.engine || '';
    }
  }

  // In all other cases, we'll return the engine of the default package.
  const packages = getPackages(state, pdbId);
  return packages[index].engine || '';
};

export const getPower = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
  packageId?: string,
): string => {
  // If a package is selected,
  const pkgId = packageId || getPackageIdFromUrl(state);
  if (pkgId) {
    // we'll need to retrieve the package.
    const currentPackage = getPackage(state, Number(pkgId), pdbId);
    // If the package can be retrieved successfully,
    if (currentPackage) {
      // we can return the package power.
      return currentPackage.power || '';
    }
  }

  // In all other cases, we'll return the power of the default package.
  const packages = getPackages(state, pdbId);
  return packages[index].power || '';
};

export const getReservationExpires = (state: GlobalState,
  pdbId: ?string = null, index?: number = 0): string => {
  const packages = getPackages(state, pdbId);
  return packages[index].reservation_expires || '';
};

export const getConsumption = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
): string => {
  const packages = getPackages(state, pdbId);
  return packages[index].consumption || '';
};

export const getWeight = (
  state: GlobalState,
  pdbId: ?string = null,
  index?: number = 0,
): string => {
  const packages = getPackages(state, pdbId);
  return packages[index].weight || '';
};

export const getDeliveryTime = (
  state: GlobalState,
  pdbId: ?string = null,
  packageId?: ?string,
): ?number => {
  const pkgId = packageId || getPackageIdFromUrl(state);
  const selectedPackage = getPackage(state, Number(pkgId), pdbId);
  return selectedPackage && selectedPackage.mapped ? selectedPackage.mapped.deliverytime : null;
};

// @todo we should put this correctly int he store instead of formatting it here
// (this is way more costly)
export const getPackageContentsTable = (
  state: GlobalState,
  pdbId: ?string = null,
): PackageContentTable => {
  const packages = getPackages(state, pdbId);

  return packages
    .map(({ id, contains = [] }) => [id, contains])
    .reduce((groups, [id, contains]) => (
      groups.concat(contains.map(elem => [id, elem.group, elem.name]))
    ), [])
    .reduce((table, [id, group, name]) => ({
      ...table,
      [group]: {
        ...table[group],
        [name]: {
          // Merge existing data if there is any.
          ...(table[group] || {})[name],
          // Set the current shortcode to true.
          [id]: true,
        },
      },
    }), {});
};

export const hasSinglePackage = (state: GlobalState, pdbId: ?string = null): boolean => {
  const packages = getPackages(state, pdbId);

  return packages.length === 1;
};

export const getPackageContainsVbmrCars = (
  state: GlobalState,
  packageId: number,
  colorId: number,
  pdbId?: ?string,
): boolean => {
  const packages = getPackages(state, pdbId);
  const pack = packages.find(({ id }) => id === packageId);
  if (pack && pack.cars) {
    return !!pack.cars.find(car => car.color_id === colorId && car.isVBMR);
  }

  return false;
};

export const getCheapestVbmrPackage = (
  state: GlobalState,
  pdbId?: ?string,
): ?Package => {
  const packages = getPackages(state, pdbId);
  const packagesWithVbmr = packages.filter(pack => pack.mapped.has_vbmr);
  return Array.isArray(packagesWithVbmr) && packagesWithVbmr.length > 0
    ? getCheapestPackage(packagesWithVbmr) : null;
};

export const getCheapestVbmrColorId = (
  state: GlobalState,
  pdbId?: ?string,
): ?number => {
  const cheapestPackageWithVbmr = getCheapestVbmrPackage(state, pdbId);
  if (!cheapestPackageWithVbmr) {
    return null;
  }
  const cheapestCar: ?PackageCar = cheapestPackageWithVbmr.cars
    && cheapestPackageWithVbmr.cars.find(
      car => car.color_price === 0 && car.isVBMR === true,
    );
  return cheapestCar ? cheapestCar.color_id : null;
};

export const hasVbmr = (
  state: GlobalState,
  packageId: string,
  pdbId?: ?string,
): boolean => {
  const currentPackage = getPackage(state, Number(packageId), pdbId);
  return currentPackage ? currentPackage.mapped.has_vbmr : false;
};
